import { useRef, useEffect } from 'react'
import type { KeyboardEvent, ChangeEvent } from 'react'

interface OTPInputProps {
  value: string
  onChange: (value: string) => void
  length?: number
  disabled?: boolean
}

export function OTPInput({
  value,
  onChange,
  length = 6,
  disabled = false,
}: OTPInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus()
    }
  }, [disabled])

  const handleChange = (index: number, newValue: string) => {
    // Convert to uppercase and filter alphanumeric characters
    const upperValue = newValue.toUpperCase().replace(/[^0-9A-Z]/g, '')
    
    // Only allow single alphanumeric character
    if (!/^[0-9A-Z]?$/.test(upperValue)) {
      return
    }

    const newOtp = value.split('')
    newOtp[index] = upperValue
    onChange(newOtp.join(''))

    // Auto-focus next input
    if (upperValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData
      .getData('text/plain')
      .toUpperCase()
      .replace(/[^0-9A-Z]/g, '')
      .slice(0, length)

    if (/^[0-9A-Z]+$/.test(pastedData)) {
      onChange(pastedData.padEnd(length, ''))

      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, length - 1)
      inputRefs.current[nextIndex]?.focus()
    }
  }

  return (
    <div className='flex gap-2 justify-center' onPaste={handlePaste}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el
          }}
          type='text'
          inputMode='text'
          maxLength={1}
          className='w-12 h-12 text-center border border-input bg-background rounded-md text-lg font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed'
          value={value[index] || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleChange(index, e.target.value)
          }
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(index, e)
          }
          disabled={disabled}
        />
      ))}
    </div>
  )
}
